import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Smooth full-screen lightbox.
 * Props:
 *   items: [{ src, alt }]
 *   index: number | null  (null = closed)
 *   onClose: () => void
 *   onPrev / onNext: () => void
 */
export default function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const open = index !== null && index >= 0;
  const current = open ? items[index] : null;

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    },
    [open, onClose, onPrev, onNext]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={onClose}
          data-testid="lightbox-backdrop"
        >
          {/* Close */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white inline-flex items-center justify-center transition"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Prev */}
          {onPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev();
              }}
              aria-label="Previous photo"
              data-testid="lightbox-prev"
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white inline-flex items-center justify-center transition"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next */}
          {onNext && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              aria-label="Next photo"
              data-testid="lightbox-next"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white inline-flex items-center justify-center transition"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Image */}
          <motion.figure
            key={current?.src}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-figure"
          >
            <img
              src={current?.src}
              alt={current?.alt || ""}
              className="max-w-[92vw] max-h-[80vh] w-auto h-auto object-contain rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
            />
            <div className="mt-3 text-xs text-white/45">
              {index + 1} / {items.length}
            </div>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
